import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { documentTitle, type ModelDocumentEntry } from '@rock/manuals';
import type { ModelDocumentId } from '@rock/types';
import { type FC, useId, useState } from 'react';
import { type ModelChangeEffect, losesServedDocument } from './modelChangeDocuments';

/** Per directly-linked document: move it along with the model, or unlink it here. */
export type DirectLinkChoice = 'add' | 'unlink';

export interface ModelChangeDecision {
    choices: Map<ModelDocumentId, DirectLinkChoice>;
    searchAgain: boolean;
}

/**
 * Asked before a model's type or brand changes when its documents would not follow: each document a
 * person linked here is either told it is for the new type or brand too, or unlinked; documents linked by
 * a rule that stops reaching the model are listed as leaving. Searching again files a fresh document
 * search, on by default when the model would lose a document the app shows.
 */
export const ModelChangeDocumentsDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** "Change type to Microwave", "Link KitchenAid" — the change being asked about. */
    title: string;
    /** What gets added to a document that moves along: "Microwave", "KitchenAid". */
    addition: string | undefined;
    effect: ModelChangeEffect;
    saving: boolean;
    onConfirm: (decision: ModelChangeDecision) => Promise<boolean>;
}> = ({ open, onOpenChange, ...rest }) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-xl">
            {/* Radix unmounts the content while closed, so the choices reset on every open. */}
            <ModelChangeForm {...rest} onClose={() => onOpenChange(false)} />
        </DialogContent>
    </Dialog>
);

const ModelChangeForm: FC<{
    title: string;
    addition: string | undefined;
    effect: ModelChangeEffect;
    saving: boolean;
    onConfirm: (decision: ModelChangeDecision) => Promise<boolean>;
    onClose: () => void;
}> = ({ title, addition, effect, saving, onConfirm, onClose }) => {
    const [choices, setChoices] = useState(
        () => new Map(effect.direct.map((entry) => [entry.doc.id, (addition ? 'add' : 'unlink') as DirectLinkChoice])),
    );
    const unlinked = effect.direct.filter((entry) => choices.get(entry.doc.id) === 'unlink');
    // Follows the choices (on when the model would lose a document the app shows) until the reviewer sets it.
    const [searchChoice, setSearchChoice] = useState<boolean | undefined>(undefined);
    const searchAgain = searchChoice ?? losesServedDocument(effect, unlinked);
    const searchId = useId();

    const choose = (docId: ModelDocumentId, choice: DirectLinkChoice) =>
        setChoices((prev) => new Map(prev).set(docId, choice));

    return (
        <>
            <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
                <DialogDescription>Some of this model’s documents are for its current type or brand.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 text-sm">
                {effect.direct.map((entry) => (
                    <DirectLinkRow
                        key={entry.doc.id}
                        entry={entry}
                        addition={addition}
                        choice={choices.get(entry.doc.id) ?? 'unlink'}
                        saving={saving}
                        onChoose={(choice) => choose(entry.doc.id, choice)}
                    />
                ))}
                {effect.leaving.length > 0 && (
                    <div className="space-y-1">
                        <div className="font-medium">Leaves this model (linked by rule)</div>
                        <ul className="list-disc pl-5 text-muted-foreground">
                            {effect.leaving.map((entry) => (
                                <li key={entry.doc.id}>{documentTitle(entry.doc)}</li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className="flex items-center gap-2">
                    <Checkbox
                        id={searchId}
                        checked={searchAgain}
                        onCheckedChange={(value) => setSearchChoice(value === true)}
                        disabled={saving}
                    />
                    <Label htmlFor={searchId} className="cursor-pointer font-normal">
                        Search again for this model’s documents
                    </Label>
                </div>
            </div>
            <DialogFooter>
                <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    type="button"
                    disabled={saving}
                    onClick={async () => {
                        if (await onConfirm({ choices, searchAgain })) onClose();
                    }}
                >
                    Apply
                </Button>
            </DialogFooter>
        </>
    );
};

const DirectLinkRow: FC<{
    entry: ModelDocumentEntry;
    addition: string | undefined;
    choice: DirectLinkChoice;
    saving: boolean;
    onChoose: (choice: DirectLinkChoice) => void;
}> = ({ entry, addition, choice, saving, onChoose }) => {
    const group = useId();
    const options: { value: DirectLinkChoice; label: string }[] = [
        ...(addition ? [{ value: 'add' as const, label: `Add ${addition} to this document` }] : []),
        { value: 'unlink', label: 'Unlink it from this model' },
    ];
    return (
        <div className="space-y-1 rounded-md border p-2.5">
            <div className="font-medium">{documentTitle(entry.doc)}</div>
            {options.map((option) => (
                <label key={option.value} className="flex cursor-pointer items-center gap-2">
                    <input
                        type="radio"
                        name={group}
                        checked={choice === option.value}
                        onChange={() => onChoose(option.value)}
                        disabled={saving}
                        className="h-3.5 w-3.5"
                    />
                    {option.label}
                </label>
            ))}
            {choice === 'add' && entry.doc.coverage && (
                <div className="text-xs text-muted-foreground">Its rule then also reaches models filed that way.</div>
            )}
        </div>
    );
};
